import type { Point } from '@ikegps/core-api';
import { type ApprovableInsightDrawable, type DrawableWithLabel } from './drawable';
import { CollectionModelDrawableLabel } from './label.drawable';
import type { SVGRenderOptions } from '../../../draw';
import { StyleConfig } from '../../../draw/theme';
import { IkeId } from '../../../id';
import { IkeFetchableRaceError } from '../../../rest/fetchable';
import { IkeUnit } from '../../../unit';
import type { CollectionModelFieldVerticalSeparation } from '../fields/vertical.separation.field';
import { CollectionModelDrawable } from './drawable';
import { CollectionModelDrawableState } from './drawable.state';
import type { CollectionModelDrawableMarker } from './marker.drawable';

/**
 * Draw a vertical separation annotation with a label and an optional minimum distance check.
 */
export class CollectionModelDrawableVerticalSeparation extends CollectionModelDrawable implements DrawableWithLabel {
	/**
	 * @throws IkeFetchableRaceError if the capture fails to load due to a race condition.
	 */
	static createWithLabel(
		drawableState: CollectionModelDrawableState,
		topMarkerId: string,
		bottomMarkerId: string,
		fieldId: string
	) {
		const id = IkeId.generate();
		const label = new CollectionModelDrawableLabel(drawableState, `${id}:label`, 0, 0, id);
		const drawable = new CollectionModelDrawableVerticalSeparation(
			drawableState,
			topMarkerId,
			bottomMarkerId,
			fieldId,
			label,
			id
		);
		try {
			drawable.moveLabelToDefaultPosition();
		} catch (error: unknown) {
			if (error instanceof IkeFetchableRaceError) {
				// TODO: Add logging to IkeModelCollectionReader and log a warning here.

				// Ignore the error and return the new drawable as usual so that the drawable state
				// is not affected by the race condition.
				// The label will be in the wrong position but at least the state will be correct otherwise.
				return drawable;
			}
			throw error;
		}
		return drawable;
	}

	constructor(
		collectionModelDrawableState: CollectionModelDrawableState,
		topMarkerId: string,
		bottomMarkerId: string,
		fieldId: string,
		label: CollectionModelDrawableLabel,
		drawableId: string = IkeId.generate()
	) {
		super(collectionModelDrawableState, drawableId);
		this._topMarkerId = topMarkerId;
		this._bottomMarkerId = bottomMarkerId;
		this._fieldId = fieldId;
		this._label = label;
	}

	private _topMarkerId: string;
	private _bottomMarkerId: string;

	private _label: CollectionModelDrawableLabel;

	public get label() {
		return this._label;
	}

	getLabelConnectionPoint(options: SVGRenderOptions): Point {
		return this.getLabelConnectionPointForSide(this.label.isOnLeftSide(), options.draw.state.zoom);
	}

	/**
	 * Pass leftSide = true for left, or leftSide = false for right.
	 * @throws IkeFetchableRaceError if the capture fails to load due to a race condition.
	 */
	private getLabelConnectionPointForSide(leftSide: boolean, zoom: number = 1): Point {
		const indexOffset = this.indexInGroupWithSameTopMarker() * StyleConfig.STROKE_WIDTH * 4;
		const markerOffsetWidth = 200 - zoom * 10;
		return {
			// If label is on left side, draw line to middle of vertical separation on the left side of the pole.
			// Otherwise, draw to middle of the separation on the right side of the pole.
			x: leftSide
				? this.topMarker.x - markerOffsetWidth + indexOffset
				: this.topMarker.x + markerOffsetWidth - indexOffset,
			y: this.topMarker.y + (this.distanceBetweenMarkersInPixels + StyleConfig.STROKE_WIDTH) / 2,
		};
	}

	public get topMarker(): CollectionModelDrawableMarker {
		return this.drawableState.getDrawable<CollectionModelDrawableMarker>(this._topMarkerId);
	}

	public get bottomMarker(): CollectionModelDrawableMarker {
		return this.drawableState.getDrawable<CollectionModelDrawableMarker>(this._bottomMarkerId);
	}

	public get topMarkerId(): string {
		return this._topMarkerId;
	}

	public get bottomMarkerId(): string {
		return this._bottomMarkerId;
	}

	public get distanceBetweenMarkersInPixels(): number {
		return this.bottomMarker.y - this.topMarker.y;
	}

	public get distanceBetweenMarkersInMeters(): number {
		return this.drawableState.collection.state
			.getGeometry(this.drawableState.captureId)
			.calculateDistance(this.bottomMarker.getVector(), this.topMarker.getVector());
	}

	/**
	 * @throws IkeFetchableRaceError if the capture fails to load due to a race condition.
	 */
	private verticalSeparationsWithSameTopMarker(): CollectionModelDrawableVerticalSeparation[] {
		const result: CollectionModelDrawableVerticalSeparation[] = [];
		for (const drawable of this.drawableState.getAllDrawables()) {
			if (!(drawable instanceof CollectionModelDrawableVerticalSeparation)) {
				continue;
			}
			if (drawable.topMarkerId !== this.topMarkerId) {
				continue;
			}
			// Skip drawables with unresolvable field references (e.g. orphaned clones
			// from cross-collection copy that retain the source collection's field ID).
			if (drawable.field == null) {
				continue;
			}
			result.push(drawable);
		}
		result.sort((a, b) => (a.field?.value ?? 0) - (b.field?.value ?? 0));
		return result;
	}

	/**
	 * Returns the index of this vertical separation in the list of vertical separations
	 * that have the same top marker, when ordered by field value (distance between top and bottom markers).
	 * @throws IkeFetchableRaceError if the capture fails to load due to a race condition.
	 */
	public indexInGroupWithSameTopMarker(): number {
		return this.verticalSeparationsWithSameTopMarker().indexOf(this);
	}

	private _fieldId: string;

	public get fieldId(): string {
		return this._fieldId;
	}

	private get field(): CollectionModelFieldVerticalSeparation | null {
		return this.collection.getFieldDeep<CollectionModelFieldVerticalSeparation>(this._fieldId);
	}

	public get clearanceThresholdInMeters(): number | undefined {
		return this.field?.schema.clearanceThresholdInMeters;
	}

	/**
	 * Returns true if the distance between the top and bottom markers is greater than the clearance defined on the form field.
	 * If a clearance is not defined on the form field, it returns undefined.
	 */
	public get isInClearance(): boolean | undefined {
		if (!this.clearanceThresholdInMeters) {
			return undefined;
		}
		return this.distanceBetweenMarkersInMeters > this.clearanceThresholdInMeters;
	}

	/**
	 * @deprecated Use {@link getLabelDisplayText} instead.
	 */
	public get text(): string {
		return this.field?.schema.displayName ?? '';
	}

	public getLabelDisplayText(options: SVGRenderOptions): string[] {
		// The distance between the markers formatted as a distance string (e.g. 66" or 52cm).
		const clearanceText = IkeUnit.display(
			this.distanceBetweenMarkersInMeters,
			options.units === 'meters' ? IkeUnit.CENTIMETER : IkeUnit.INCH,
			undefined,
			undefined,
			0
		);

		if (!this.clearanceThresholdInMeters) {
			return [clearanceText + ' - ' + (this.field?.schema.displayName ?? '')];
		}

		return [(this.isInClearance ? '✅ ' : '❌ ') + clearanceText + ' - ' + (this.field?.schema.displayName ?? '')];
	}

	override get x(): number {
		return this.topMarker.x;
	}

	override get y(): number {
		return this.topMarker.y;
	}

	/**
	 * @throws IkeFetchableRaceError if the capture fails to load due to a race condition.
	 */
	moveLabelToDefaultPosition(): void {
		this.label.snapToLeftSide();
		this.label.move(this.label.x, this.getLabelConnectionPointForSide(true, 1).y);
	}

	override remove() {
		super.remove();
		// Remove the label too, unless it has already been removed
		if (this.drawableState.hasDrawable(this._label.id)) {
			this.label.remove();
		}
	}

	override get isInsightMeasurement(): boolean {
		return false;
	}

	override get isVerticalSeparation(): boolean {
		return true;
	}

	override isApprovableInsightDrawable(): this is ApprovableInsightDrawable {
		return false;
	}

	override get isVisible(): boolean {
		return true;
	}

	override clone(newParentDrawableState: CollectionModelDrawableState): CollectionModelDrawableVerticalSeparation {
		if (!newParentDrawableState.hasDrawable(this._label.id)) {
			this.label.clone(newParentDrawableState);
		}
		return new CollectionModelDrawableVerticalSeparation(
			newParentDrawableState,
			this._topMarkerId,
			this._bottomMarkerId,
			this._fieldId,
			newParentDrawableState.getDrawable(this.label.id),
			this.id
		);
	}

	override equals(other: CollectionModelDrawable): boolean {
		return (
			other instanceof CollectionModelDrawableVerticalSeparation &&
			this._topMarkerId === other._topMarkerId &&
			this._bottomMarkerId === other._bottomMarkerId &&
			this._fieldId === other._fieldId &&
			this._equals(other)
		);
	}
}
